import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";

import { Menu } from "@/shared/components/Menu";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoMenuItem } from "@/shared/components/RobotoMenuItem";
import { RobotoStyledPopoverMenu } from "@/shared/components/RobotoStyledPopoverMenu";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { createEmptyLayout, LayoutRecord } from "@/shared/domain/layouts";
import { LayoutAccessibility } from "@/shared/domain/layouts/LayoutRecord";
import { Button } from "@/shared/foundation/primitives";

import { useActiveLayout } from "../../WorkspaceCtx/hooks";

import { LayoutFolderGroup, LayoutOption } from "./layoutFolders";
import { LayoutMenuItem } from "./LayoutMenuItem";
import { LayoutMenuSection } from "./LayoutMenuSection";
import styles from "./SelectLayoutMenu.module.css";
import { useLayoutOptions } from "./useLayoutOptions";

interface SelectLayoutMenuProps {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    layout: LayoutRecord,
  ) => void;
  onOpenFolderMenu: (
    event: React.MouseEvent<HTMLElement>,
    group: LayoutFolderGroup,
  ) => void;
  onSaveLayout: () => void;
  onSelectLayout: (layout: LayoutRecord) => void;
  onMoveSelected: (layouts: LayoutRecord[]) => void;
  onDeleteSelected: (layouts: LayoutRecord[]) => void;
  onRenameLayout: (layout: LayoutRecord) => void;
  onShareLayout: (layout: LayoutRecord) => void;
  onNewFolder: () => void;
}

export function SelectLayoutMenu({
  anchorEl,
  isOpen,
  onClose,
  ...menuItemProps
}: SelectLayoutMenuProps) {
  return (
    <Menu
      id="select-layout-menu"
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          style: {
            // 900px caps the height on large screens; 75vh keeps it off the edges on small ones.
            maxHeight: "min(900px, 75vh)",
            // Fixed 320px: wide enough for the search placeholder, and capped so long
            // layout/folder names ellipsize instead of stretching the whole menu.
            minWidth: 320,
            maxWidth: 320,
          },
        },
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <LayoutMenuItems {...menuItemProps} />
    </Menu>
  );
}

type LayoutMenuItemsProps = Omit<
  SelectLayoutMenuProps,
  "anchorEl" | "isOpen" | "onClose"
>;

function LayoutMenuItems({
  onSaveLayout,
  onSelectLayout,
  onOpenContextMenu,
  onOpenFolderMenu,
  onMoveSelected,
  onDeleteSelected,
  onRenameLayout,
  onShareLayout,
  onNewFolder,
}: LayoutMenuItemsProps) {
  const [activeLayout] = useActiveLayout();
  const [search, setSearch] = React.useState("");
  const [selectMode, setSelectMode] = React.useState(false);
  const [selected, setSelected] = React.useState<Map<string, LayoutRecord>>(
    new Map(),
  );
  // Personal/Team start expanded.
  const [personalExpanded, setPersonalExpanded] = React.useState(true);
  const [teamExpanded, setTeamExpanded] = React.useState(true);
  // Collapse-all / expand-all broadcasts a bulk target to the folders, whose expansion is
  // otherwise their own local state.
  const [folderBulk, setFolderBulk] = React.useState<FolderBulkExpansion>(null);
  const layoutOptions = useLayoutOptions(search);

  if (layoutOptions.isPending) {
    return (
      <MenuItem sx={{ typography: "body2", gap: 1.5 }} disableRipple>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading layouts...</Typography>
      </MenuItem>
    );
  }

  if (layoutOptions.isError) {
    return (
      <MenuItem
        disableRipple
        sx={{
          typography: "body2",
          color: "error.main",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <p>Failed to load layouts</p>
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            void layoutOptions.refetch();
          }}
          startIcon={<RefreshIcon />}
        >
          Try Again
        </Button>
      </MenuItem>
    );
  }

  const isSearching = search.trim().length > 0;

  const visibleOptions = [
    ...layoutOptions.personalGroups,
    ...layoutOptions.teamGroups,
  ].flatMap((group) => group.layouts);
  const allVisibleSelected =
    visibleOptions.length > 0 &&
    visibleOptions.every((option) => selected.has(option.id));

  const toggleSelect = (layout: LayoutRecord) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(layout.layout_id)) {
        next.delete(layout.layout_id);
      } else {
        next.set(layout.layout_id, layout);
      }
      return next;
    });
  };

  const setOptionsSelected = (options: LayoutOption[], select: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      options.forEach((option) => {
        if (select) {
          next.set(option.id, option.record);
        } else {
          next.delete(option.id);
        }
      });
      return next;
    });
  };

  const toggleSelectAll = () =>
    setOptionsSelected(visibleOptions, !allVisibleSelected);

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Map());
  };

  // Run a selection action, then leave select mode (the selection is spent afterward).
  // Whether the outer menu also closes is up to the handler: move/delete close it,
  // rename/share leave it open behind their dialog.
  const runSelectionAction = (action: () => void) => {
    action();
    exitSelectMode();
  };

  const selectedLayouts = [...selected.values()];

  // The toggle expands everything when the sections are all collapsed, and collapses
  // everything otherwise — sections directly, folders via the bulk broadcast.
  const allSectionsCollapsed = !personalExpanded && !teamExpanded;
  const toggleAllExpansion = () => {
    const nextExpanded = allSectionsCollapsed;
    setPersonalExpanded(nextExpanded);
    setTeamExpanded(nextExpanded);
    setFolderBulk({ expanded: nextExpanded });
  };

  const itemHandlers: LayoutItemHandlers = {
    selectMode,
    selectedIds: selected,
    onToggleSelect: toggleSelect,
    onSetOptionsSelected: setOptionsSelected,
    activeLayoutId: activeLayout.layout_id,
    onSelect: onSelectLayout,
    onOpenContextMenu,
    onOpenFolderMenu,
    folderBulk,
    isSearching,
  };

  return (
    <>
      <RobotoMenuItem
        eventName={"OpenSaveLayoutConfirmationClicked"}
        onClick={onSaveLayout}
        disableRipple
        sx={{ py: 1.2 }}
      >
        <SaveIcon fontSize="small" sx={{ color: "grey.700" }} />
        <Typography variant="body2">Save layout</Typography>
      </RobotoMenuItem>
      <RobotoMenuItem
        disableRipple
        eventName={"ClearLayoutClicked"}
        onClick={() => onSelectLayout(createEmptyLayout())}
        sx={{ py: 1.2 }}
      >
        <ClearIcon fontSize="small" sx={{ color: "grey.700" }} />
        <Typography variant="body2">Clear layout</Typography>
      </RobotoMenuItem>
      <Divider />

      {/* Keep keydown off the Menu so typing here doesn't trigger its typeahead item navigation. */}
      <Box sx={{ px: 2, py: 1 }} onKeyDown={(event) => event.stopPropagation()}>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search layouts and folders"
          size="small"
          fullWidth
          // Match the height of the layout dropdown field that opens this menu (2rem), and the
          // 14px text size of the menu rows around it.
          sx={{
            "& .MuiOutlinedInput-root": { height: "2rem" },
            "& .MuiOutlinedInput-input": { py: 0, fontSize: "0.875rem" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: isSearching ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>

      <Box
        // Split the vertical padding so the button drops off the search box a little, tightening
        // the gap down to the first section without moving that section.
        sx={{
          px: 2,
          pt: 0.5,
          pb: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {selectMode ? (
          <>
            <LayoutSelectionActions
              selectedLayouts={selectedLayouts}
              allVisibleSelected={allVisibleSelected}
              onToggleSelectAll={toggleSelectAll}
              onMove={() =>
                runSelectionAction(() => onMoveSelected(selectedLayouts))
              }
              onDelete={() =>
                runSelectionAction(() => onDeleteSelected(selectedLayouts))
              }
              onRename={(layout) =>
                runSelectionAction(() => onRenameLayout(layout))
              }
              onShare={(layout) =>
                runSelectionAction(() => onShareLayout(layout))
              }
            />
            <Button
              variant="text"
              size="small"
              sx={{ height: "32px" }}
              onClick={exitSelectMode}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            {/* Split the row evenly so the two buttons fill it instead of leaving dead space. */}
            <Button
              variant="outlined"
              size="small"
              sx={{ flex: 1, height: "32px" }}
              onClick={() => setSelectMode(true)}
            >
              Select
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CreateNewFolderIcon />}
              sx={{ flex: 1, height: "32px" }}
              onClick={onNewFolder}
            >
              New Folder
            </Button>
            <RobotoTooltip
              title={allSectionsCollapsed ? "Expand all" : "Collapse all"}
              enterDelay={700}
              enterNextDelay={700}
            >
              <IconButton
                size="small"
                aria-label={
                  allSectionsCollapsed ? "Expand all" : "Collapse all"
                }
                onClick={toggleAllExpansion}
                sx={{ flexShrink: 0 }}
              >
                {allSectionsCollapsed ? (
                  <UnfoldMoreIcon fontSize="small" />
                ) : (
                  <UnfoldLessIcon fontSize="small" />
                )}
              </IconButton>
            </RobotoTooltip>
          </>
        )}
      </Box>

      <LayoutMenuSection
        title="Personal Layouts"
        hasNoLayouts={layoutOptions.personalGroups.length === 0}
        hasNoLayoutsMessage={
          isSearching
            ? "No matching personal layouts"
            : "No personal layouts yet"
        }
        // Search forces the section open so matches show; otherwise it honors the toggle,
        // collapsible even in select mode.
        expanded={isSearching || personalExpanded}
        onToggle={() => setPersonalExpanded((expanded) => !expanded)}
      >
        <LayoutGroups groups={layoutOptions.personalGroups} {...itemHandlers} />
      </LayoutMenuSection>

      <LayoutMenuSection
        title="Team Layouts"
        hasNoLayouts={layoutOptions.teamGroups.length === 0}
        hasNoLayoutsMessage={
          isSearching ? "No matching team layouts" : "No team layouts yet"
        }
        expanded={isSearching || teamExpanded}
        onToggle={() => setTeamExpanded((expanded) => !expanded)}
      >
        <LayoutGroups groups={layoutOptions.teamGroups} {...itemHandlers} />
      </LayoutMenuSection>
    </>
  );
}

/** The `Selected (N)` dropdown of bulk actions, styled like the dataset selection menu. */
function LayoutSelectionActions({
  selectedLayouts,
  allVisibleSelected,
  onToggleSelectAll,
  onMove,
  onDelete,
  onRename,
  onShare,
}: {
  selectedLayouts: LayoutRecord[];
  allVisibleSelected: boolean;
  onToggleSelectAll: () => void;
  onMove: () => void;
  onDelete: () => void;
  onRename: (layout: LayoutRecord) => void;
  onShare: (layout: LayoutRecord) => void;
}) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const count = selectedLayouts.length;
  // Rename and Share only make sense for a single layout.
  const single = count === 1 ? selectedLayouts[0] : null;

  const close = () => setAnchorEl(null);
  const run = (action: () => void) => {
    close();
    action();
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        sx={{ height: "32px" }}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        Selected ({count})
      </Button>
      <RobotoStyledPopoverMenu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={close}
      >
        <MenuItem onClick={() => run(onToggleSelectAll)}>
          <SelectAllIcon />
          {allVisibleSelected ? "Deselect all" : "Select all"}
        </MenuItem>
        <RobotoMenuItem
          eventName="MoveSelectedLayoutsOptionClicked"
          eventProperties={{ count }}
          disabled={count === 0}
          onClick={() => run(onMove)}
        >
          <DriveFileMoveOutlinedIcon />
          Move to folder
        </RobotoMenuItem>
        {single && (
          <MenuItem onClick={() => run(() => onRename(single))}>
            <EditOutlinedIcon />
            Rename
          </MenuItem>
        )}
        {single && single.accessibility === LayoutAccessibility.User && (
          <MenuItem onClick={() => run(() => onShare(single))}>
            <GroupAddOutlinedIcon />
            Share with team
          </MenuItem>
        )}
        <MenuItem
          disabled={count === 0}
          sx={{ color: "error.main" }}
          onClick={() => run(onDelete)}
        >
          <DeleteOutlineIcon />
          Delete
        </MenuItem>
      </RobotoStyledPopoverMenu>
    </>
  );
}

/** A bulk expand/collapse target broadcast to every folder. */
type FolderBulkExpansion = { expanded: boolean } | null;

interface LayoutItemHandlers {
  activeLayoutId?: string;
  selectMode: boolean;
  selectedIds: Map<string, LayoutRecord>;
  onToggleSelect: (layout: LayoutRecord) => void;
  onSetOptionsSelected: (options: LayoutOption[], selected: boolean) => void;
  onSelect: (layout: LayoutRecord) => void;
  onOpenContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    layout: LayoutRecord,
  ) => void;
  onOpenFolderMenu: (
    event: React.MouseEvent<HTMLElement>,
    group: LayoutFolderGroup,
  ) => void;
  folderBulk: FolderBulkExpansion;
  isSearching: boolean;
}

function LayoutOptionItem({
  option,
  isNested,
  activeLayoutId,
  selectMode,
  selectedIds,
  onToggleSelect,
  onSelect,
  onOpenContextMenu,
}: {
  option: LayoutFolderGroup["layouts"][number];
  isNested?: boolean;
} & LayoutItemHandlers) {
  return (
    <LayoutMenuItem
      isNested={isNested}
      isActive={activeLayoutId === option.id}
      isDefault={option.isDefault}
      layout={option.record}
      onSelect={onSelect}
      onOpenContextMenu={onOpenContextMenu}
      selectMode={selectMode}
      isSelected={selectedIds.has(option.id)}
      onToggleSelect={onToggleSelect}
    />
  );
}

/** The null-folder group renders flat; named folders render as collapsible sections. */
function LayoutGroups({
  groups,
  ...handlers
}: { groups: LayoutFolderGroup[] } & LayoutItemHandlers) {
  return (
    <>
      {groups.map((group) => {
        if (group.folder === null) {
          return (
            <React.Fragment key="__ungrouped__">
              {group.layouts.map((option) => (
                <LayoutOptionItem
                  key={option.id}
                  option={option}
                  {...handlers}
                />
              ))}
            </React.Fragment>
          );
        }

        return (
          <LayoutFolderGroupView
            key={group.folder}
            folder={group.folder}
            group={group}
            {...handlers}
          />
        );
      })}
    </>
  );
}

function LayoutFolderGroupView({
  folder,
  group,
  ...handlers
}: { folder: string; group: LayoutFolderGroup } & LayoutItemHandlers) {
  const {
    selectMode,
    selectedIds,
    onSetOptionsSelected,
    onOpenFolderMenu,
    folderBulk,
    isSearching,
  } = handlers;
  // Folders start collapsed; a search forces them open so matching layouts inside still show.
  const [isExpanded, setIsExpanded] = React.useState(false);
  const toggleExpanded = () => setIsExpanded((expanded) => !expanded);
  const isFolderOpen = isSearching || isExpanded;

  // Collapse-all / expand-all drives every folder to the same state; a new bulk signal
  // (identity change) overrides whatever the folder's own toggle last set.
  React.useEffect(() => {
    if (folderBulk) {
      setIsExpanded(folderBulk.expanded);
    }
  }, [folderBulk]);

  const allChildrenSelected =
    group.layouts.length > 0 &&
    group.layouts.every((option) => selectedIds.has(option.id));
  const someChildrenSelected = group.layouts.some((option) =>
    selectedIds.has(option.id),
  );

  const chevron = isFolderOpen ? (
    <ExpandMoreIcon className={styles.folderChevron} />
  ) : (
    <ChevronRightIcon className={styles.folderChevron} />
  );

  return (
    <>
      <MenuItem
        disableRipple
        onClick={(event) => {
          event.stopPropagation();
          // Select mode turns the row into a select-all toggle; the trailing chevron button
          // collapses instead. Browse mode collapses on the row click itself.
          if (selectMode) {
            onSetOptionsSelected(group.layouts, !allChildrenSelected);
          } else {
            toggleExpanded();
          }
        }}
        className={styles.folderHeader}
      >
        {/* Select mode swaps the leading chevron for a select-all checkbox in the same 20px slot. */}
        {selectMode ? (
          <Checkbox
            checked={allChildrenSelected}
            indeterminate={someChildrenSelected && !allChildrenSelected}
            readOnly
            size="small"
            tabIndex={-1}
            disableRipple
            className={styles.folderCheckbox}
            sx={{ p: 0 }}
            inputProps={{ "aria-label": `Select all layouts in ${folder}` }}
          />
        ) : (
          chevron
        )}
        <Typography
          variant="body2"
          fontWeight={600}
          className={styles.folderName}
          title={folder}
        >
          {folder}
        </Typography>
        {/* Browse mode's trailing slot holds the folder actions; select mode reuses it for a
            collapse toggle, since the leading slot is now the checkbox and the row click selects. */}
        {selectMode ? (
          <IconButton
            size="small"
            aria-label={
              isFolderOpen ? `Collapse ${folder}` : `Expand ${folder}`
            }
            onClick={(event) => {
              event.stopPropagation();
              toggleExpanded();
            }}
          >
            {chevron}
          </IconButton>
        ) : (
          <RobotoIconButton
            eventName="LayoutFolderContextMenuOpened"
            eventProperties={{ folder }}
            size="small"
            aria-label="Folder actions"
            onClick={(event) => onOpenFolderMenu(event, group)}
          >
            <MoreVertIcon className={styles.folderMenuIcon} />
          </RobotoIconButton>
        )}
      </MenuItem>
      {isFolderOpen &&
        group.layouts.map((option) => (
          <LayoutOptionItem
            key={option.id}
            option={option}
            isNested
            {...handlers}
          />
        ))}
    </>
  );
}
